import querystring from "query-string";
import { fetchNetlifyFunctions } from "../../../services/netlify-functions";
import {
  DeliveryAddressType,
  ProductVariantType,
  RetailProductAvailabilityType
} from "../../../types/airgraft";

type RetailProvidersType = Readonly<
  Queries.ProductRetailProvidersFragmentQuery["allDatoCmsRetailProvider"]["nodes"]
>;

/**
 * Fetch retail provider product availability using productId and retailProviderId
 */
export async function fetchRetailProviderProductAvailability(
  productId: string,
  retailProviderId: string
): Promise<RetailProductAvailabilityType> {
  const response = await fetchNetlifyFunctions(
    querystring.stringifyUrl({
      url: "retail-provider-product-availability",
      query: {
        productId,
        retailProviderId
      }
    })
  );
  if (!response.ok) {
    throw new Error(
      "Something went wrong trying to fetch retail provider product availability"
    );
  }
  const json = await response.json();
  return json as RetailProductAvailabilityType;
}

/**
 * Filter retail providers that match with a product
 */
export function filterRetailProvidersForProduct(
  retailProviders: RetailProvidersType,
  productVariant: ProductVariantType,
  subcountry: string
): RetailProvidersType {
  return retailProviders.filter(
    provider =>
      // Filter product brands
      (provider.enabledBrands.length === 0 ||
        provider.enabledBrands
          .map(b => b.slug)
          .includes(productVariant.brand.slug)) &&
      // Filter subcountries
      (provider.enabledSubcountries.length === 0 ||
        provider.enabledSubcountries.map(s => s.slug).includes(subcountry))
  );
}

/**
 * Sort retail providers by closest to user delivery address
 */
export function sortRetailProvidersByClosestToAddress(
  retailProviders: RetailProvidersType,
  deliveryAddress: DeliveryAddressType
): RetailProvidersType {
  // Convert degrees to radians
  function degreesToRadians(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }

  // // Calculate the distance between two geolocations using the Haversine formula
  function calculateDistanceBetweenPoints(
    geolocation1: { latitude: number; longitude: number },
    geolocation2: { latitude: number; longitude: number }
  ): number {
    const earthRadiusKm = 6371;
    const dLat = degreesToRadians(
      geolocation2.latitude - geolocation1.latitude
    );
    const dLon = degreesToRadians(
      geolocation2.longitude - geolocation1.longitude
    );

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(degreesToRadians(geolocation1.latitude)) *
        Math.cos(degreesToRadians(geolocation1.latitude)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusKm * c;
  }

  // Calculate and assign the distance to each object
  const retailProvidersWithDistance = retailProviders.map(retailProvider => {
    if (!retailProvider.geolocation) {
      return {
        ...retailProvider,
        distance: null
      };
    }
    const distance = calculateDistanceBetweenPoints(
      deliveryAddress.geolocation,
      retailProvider.geolocation
    );
    return {
      ...retailProvider,
      distance
    };
  });

  // Sort the objects by distance
  const sortedRetailProviders = retailProvidersWithDistance.sort((a, b) => {
    if (a.distance !== null && b.distance !== null) {
      return a.distance - b.distance;
    }

    // If only one of them has a distance value, prioritize the one with a valid distance.
    if (a.distance !== null) {
      return -1;
    }
    if (b.distance !== null) {
      return 1;
    }

    // If both distances are null, sort by position.
    return a.position - b.position;
  });

  return sortedRetailProviders;
}
